// Splits one render-mesh material group into spatial chunks so the GPU / frustum culling can skip
// parts of a large map. Pure data in, typed arrays out (no three.js).

export interface ChunkData {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint16Array | Uint32Array;
  min: [number, number, number];
  max: [number, number, number];
}

export interface ChunkOptions {
  /** Cell edge in the XZ plane (m). */
  cellXZ: number;
  /** Cell edge along Y (m). */
  cellY: number;
  /** Groups with at most this many triangles stay one chunk. */
  minTriangles: number;
}

export const DEFAULT_CHUNK_OPTIONS: ChunkOptions = { cellXZ: 28, cellY: 14, minTriangles: 1500 };

function cellKey(ix: number, iy: number, iz: number): number {
  // Cells are clamped to a 2^10 x 2^8 x 2^10 range; maps are far smaller than that.
  const x = Math.min(Math.max(ix + 512, 0), 1023);
  const y = Math.min(Math.max(iy + 128, 0), 255);
  const z = Math.min(Math.max(iz + 512, 0), 1023);
  return (x * 1024 + z) * 256 + y;
}

/** Copies the listed triangles into a standalone chunk with compacted vertices. */
function buildChunk(
  tris: readonly number[],
  positions: ArrayLike<number>,
  normals: ArrayLike<number>,
  uvs: ArrayLike<number>,
  indices: ArrayLike<number>,
  remap: Int32Array,
): ChunkData {
  const used: number[] = [];
  for (let t = 0; t < tris.length; t++) {
    const base = tris[t] * 3;
    for (let k = 0; k < 3; k++) {
      const v = indices[base + k];
      if (remap[v] < 0) {
        remap[v] = used.length;
        used.push(v);
      }
    }
  }
  const vcount = used.length;
  const pos = new Float32Array(vcount * 3);
  const nor = new Float32Array(vcount * 3);
  const uv = new Float32Array(vcount * 2);
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < vcount; i++) {
    const v = used[i];
    for (let k = 0; k < 3; k++) {
      const p = positions[v * 3 + k];
      pos[i * 3 + k] = p;
      nor[i * 3 + k] = normals[v * 3 + k];
      if (p < min[k]) min[k] = p;
      if (p > max[k]) max[k] = p;
    }
    uv[i * 2] = uvs[v * 2];
    uv[i * 2 + 1] = uvs[v * 2 + 1];
  }
  const idx = vcount < 65535 ? new Uint16Array(tris.length * 3) : new Uint32Array(tris.length * 3);
  for (let t = 0; t < tris.length; t++) {
    const base = tris[t] * 3;
    idx[t * 3] = remap[indices[base]];
    idx[t * 3 + 1] = remap[indices[base + 1]];
    idx[t * 3 + 2] = remap[indices[base + 2]];
  }
  for (let i = 0; i < vcount; i++) remap[used[i]] = -1;
  return { positions: pos, normals: nor, uvs: uv, indices: idx, min, max };
}

/**
 * Chunks are keyed by the cell that contains each triangle's centroid. Triangles are never split, so
 * a chunk's bounds may reach slightly past its cell. Empty input returns no chunks.
 */
export function splitGroup(
  positions: ArrayLike<number>,
  normals: ArrayLike<number>,
  uvs: ArrayLike<number>,
  indices: ArrayLike<number>,
  opts: ChunkOptions = DEFAULT_CHUNK_OPTIONS,
): ChunkData[] {
  const triCount = Math.floor(indices.length / 3);
  if (triCount === 0) return [];
  const vertexCount = Math.floor(positions.length / 3);
  const remap = new Int32Array(vertexCount).fill(-1);

  if (triCount <= opts.minTriangles) {
    const all = new Array<number>(triCount);
    for (let t = 0; t < triCount; t++) all[t] = t;
    return [buildChunk(all, positions, normals, uvs, indices, remap)];
  }

  const cells = new Map<number, number[]>();
  for (let t = 0; t < triCount; t++) {
    const a = indices[t * 3] * 3;
    const b = indices[t * 3 + 1] * 3;
    const c = indices[t * 3 + 2] * 3;
    const cx = (positions[a] + positions[b] + positions[c]) / 3;
    const cy = (positions[a + 1] + positions[b + 1] + positions[c + 1]) / 3;
    const cz = (positions[a + 2] + positions[b + 2] + positions[c + 2]) / 3;
    const key = cellKey(Math.floor(cx / opts.cellXZ), Math.floor(cy / opts.cellY), Math.floor(cz / opts.cellXZ));
    let list = cells.get(key);
    if (list === undefined) {
      list = [];
      cells.set(key, list);
    }
    list.push(t);
  }
  const out: ChunkData[] = [];
  for (const tris of cells.values()) out.push(buildChunk(tris, positions, normals, uvs, indices, remap));
  return out;
}

/** The positions and triangles of several chunks as one indexed mesh (shadow casting needs nothing else). */
export interface MergedPositions {
  positions: Float32Array;
  indices: Uint32Array;
  /** Triangle count. */
  triangles: number;
}

/** Concatenates chunks into one position-only mesh; indices are rebased onto the combined vertex list. */
export function mergePositions(chunks: readonly Pick<ChunkData, "positions" | "indices">[]): MergedPositions {
  let vertexFloats = 0;
  let indexCount = 0;
  for (const c of chunks) {
    vertexFloats += c.positions.length;
    indexCount += c.indices.length;
  }
  const positions = new Float32Array(vertexFloats);
  const indices = new Uint32Array(indexCount);
  let v = 0;
  let i = 0;
  for (const c of chunks) {
    const base = v / 3;
    positions.set(c.positions, v);
    v += c.positions.length;
    for (let k = 0; k < c.indices.length; k++) indices[i++] = c.indices[k] + base;
  }
  return { positions, indices, triangles: indexCount / 3 };
}
